<!-- A year page's sidebar (Seán, 2026-10-22, issue C1): FAQ · Highlights · Testimonials
     (assets/years/<year>/sidebar.json). Wide: the control heads the right column, in line with the
     page's title, its panel below, Highlights first. Narrow: the control takes the title's place, led
     by the title itself (VIZBI 2026), and a panel takes the page's place (Year/About.vue hides
     the page); the title brings it back. The panel's in the address (useYearSidebar.js). -->
<template>
  <div class="yearSidebar" :class="{isNarrow}">
    <SidebarTabs :tabs="tabs" :selected="selected" label="About VIZBI 2026" @select="choose" />
    <!-- switching panels: the last fades out, the next fades in, lifting a little (Seán,
         2026-10-22); at once with reduced motion -->
    <Transition name="panel" mode="out-in">
      <div
        v-if="selected"
        :id="`panel-${selected}`"
        :key="selected"
        role="tabpanel"
        class="panel"
        :aria-labelledby="`tab-${selected}`">
        <SidebarFaq
          v-if="selected === 'faq'"
          :items="content.FAQ"
          :answer="answer"
          @open="openAnswer" />
        <SidebarHighlights
          v-else-if="selected === 'highlights'"
          :items="content.HIGHLIGHTS"
          :year="year"
          :selected="highlight"
          :details="details" />
        <!-- a panel of the page's own (Year/Masterclass.vue: the Program, narrow) -->
        <slot v-else-if="$slots[selected]" :name="selected" />
        <SidebarTestimonials v-else :items="content.TESTIMONIALS" />
      </div>
    </Transition>
  </div>
</template>

<script setup>
import {computed} from "vue";
import SidebarTabs from "@/components/YearSidebar/SidebarTabs.vue";
import SidebarFaq from "@/components/YearSidebar/SidebarFaq.vue";
import SidebarHighlights from "@/components/YearSidebar/SidebarHighlights.vue";
import SidebarTestimonials from "@/components/YearSidebar/SidebarTestimonials.vue";
import useYearSidebar from "@/composables/useYearSidebar";
import {useEnvironment} from "@/composables/useEnvironment";
const props = defineProps({
  content: {type: Object, required: true}, // sidebar.json: {FAQ, HIGHLIGHTS, TESTIMONIALS, LABELS?}, LABELS: {<panel>: name}, in order
  year: {type: String, required: true},
  title: {type: String, required: true}, // "VIZBI 2026": the narrow control's first segment
  isNarrow: {type: Boolean, default: false}
});
// Testimonials on localhost only until the first is chosen (Seán, 2026-10-22, issue B1: hidden
// on staging and production); its address there shows the FAQ
// Highlights first (Seán, 2026-10-22: shown by default), then the FAQ
// a page may have its own panels and names, in its order (sidebar.json PAGES: the Masterclass
// page's "Talks", its Program on a narrow screen)
const LABELS = {...(props.content.LABELS || {highlights: "Highlights", faq: "FAQ"})};
if (useEnvironment().isDev) LABELS.testimonials = "Testimonials";
const {panel, answer, details, highlight, choose, openAnswer} = useYearSidebar();
const getTab = (key) => ({key, label: LABELS[key], panelId: `panel-${key}`});
const tabs = computed(() => {
  const panels = Object.keys(LABELS).map(getTab);
  if (!props.isNarrow) return panels;
  return [{key: "", label: props.title, panelId: "", isTitle: true}, ...panels];
});
// wide: a panel always shows, Highlights by default; narrow: none is the page itself
const DEFAULT_PANEL = "highlights";
const shown = computed(() => (panel.value in LABELS || !panel.value ? panel.value : DEFAULT_PANEL));
const selected = computed(() => shown.value || (props.isNarrow ? "" : DEFAULT_PANEL));
</script>

<style scoped>
/* below the control, every panel's content starts level with the middle of Highlights' dots,
   24px down (Seán, 2026-10-23: the reference; the FAQ was a little high, Testimonials and the
   page itself (narrow, Year/About.vue) too low). The dots sit in a 3rem band, centred */
.yearSidebar {
  --sidebar-gap: 3rem;
}
.yearSidebar :deep(.highlights .dots) {
  margin-top: calc((var(--sidebar-gap) - 24px) / 2); /* a dot's target is 24 px high */
  margin-bottom: calc((var(--sidebar-gap) - 24px) / 2);
}
.yearSidebar :deep(.highlights :is(.still, .photo)) {
  margin-top: 0;
}
/* the FAQ's first group heading: at the band's foot, its gap below as the other headings'
   (SidebarFaq.vue; Seán, 2026-10-22: centred, it stood apart from its questions); wide, the band
   a little shorter, so its capitals start level with the page's first paragraph beside it (Seán);
   on a phone, 24px down, as the other panels */
.yearSidebar :deep(.faq .group:first-child h4) {
  display: flex;
  align-items: flex-end;
  height: calc(var(--sidebar-gap) - 0.625rem);
  margin: 0;
  padding-bottom: 0.2rem;
  box-sizing: border-box;
}
.isNarrow :deep(.faq .group:first-child h4) {
  height: calc(var(--sidebar-gap) - 0.375rem);
}
.yearSidebar :deep(.testimonials header) {
  margin-top: calc(var(--sidebar-gap) / 2); /* its photo's top, 24px down */
}
.panel-enter-active,
.panel-leave-active {
  transition: opacity 0.18s ease, transform 0.18s ease;
}
.panel-enter-from {
  opacity: 0;
  transform: translateY(6px);
}
.panel-leave-to {
  opacity: 0;
}
@media (prefers-reduced-motion: reduce) {
  .panel-enter-active,
  .panel-leave-active {
    transition: none;
  }
}
/* keyboard focus in the sidebar (Seán, 2026-10-22, as #register's: FORMS.md § Focus): a solid
   ring, not the site's dotted one (global.css .showFocusOutline, !important); drawn just inside
   the control, so the column's edge never clips it. The segmented control rings as a whole
   (SidebarTabs.vue), an FAQ question as its open outline (SidebarFaq.vue) */
.yearSidebar :deep(:is(button, a):not(.segment, .question):is(:focus-visible, .showFocusOutline)) {
  outline: 2px solid var(--brand-dark-color) !important;
  outline-offset: -2px;
  border-radius: 6px;
}
.panel {
  font-size: 0.9rem;
  line-height: 1.45;
}
.isNarrow .panel {
  font-size: 1rem;
}
/* a phone's control: four segments across the screen, the text as before (SidebarTabs.vue) */
.isNarrow :deep(.segment) {
  font-size: 0.85rem;
}
</style>
